/**
 * Modal "Ejecutar cierre anual" (tesorería 2026-06).
 *
 * Acción consecuente e IRREVERSIBLE: fija el saldo arrastrado de las
 * casas al cierre del año y mueve el año contable activo a `año + 1`
 * (el dashboard y el cuadre rotan). El cierre no se deshace (no hay
 * delete por doctrina). Por eso la confirmación es fuerte: el tesorero
 * debe escribir el año dos veces y leer la advertencia.
 */
import { useState, type FormEvent } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { ApiError } from '../../lib/api/client';
import { ejecutarCierreAnual } from '../../lib/api/cierres-anuales';

interface ModalEjecutarCierreAnualProps {
  onCerrar: () => void;
  onEjecutado: () => void;
}

const ANIO_MIN = 2000;
const ANIO_MAX = 2100;

export function ModalEjecutarCierreAnual({
  onCerrar,
  onEjecutado,
}: ModalEjecutarCierreAnualProps) {
  const [anioStr, setAnioStr] = useState('');
  const [confirmStr, setConfirmStr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const anioNum = parseInt(anioStr, 10);
  const anioValido =
    /^\d{4}$/.test(anioStr.trim()) &&
    anioNum >= ANIO_MIN &&
    anioNum <= ANIO_MAX;
  const confirmado = anioValido && confirmStr.trim() === anioStr.trim();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!confirmado || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await ejecutarCierreAnual(anioNum);
      onEjecutado();
    } catch (err) {
      const msg =
        err instanceof ApiError &&
        typeof err.body === 'object' &&
        err.body !== null &&
        'detail' in err.body
          ? String((err.body as { detail: unknown }).detail)
          : 'Error de red al ejecutar el cierre.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Ejecutar cierre anual"
      onClose={onCerrar}
      footer={
        <>
          <Button
            variant="ghost"
            type="button"
            onClick={onCerrar}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            type="submit"
            form="form-ejecutar-cierre-anual"
            disabled={!confirmado || submitting}
          >
            {submitting
              ? 'Cerrando…'
              : anioValido
                ? `Ejecutar cierre ${anioNum}`
                : 'Ejecutar cierre'}
          </Button>
        </>
      }
    >
      <form
        id="form-ejecutar-cierre-anual"
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Año a cerrar <span className="text-state-danger">*</span>
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={anioStr}
            onChange={(e) => setAnioStr(e.target.value)}
            placeholder="Ej. 2026"
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            required
            autoFocus
          />
        </label>

        {anioValido && (
          <div
            className="bg-state-danger/[0.08] border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-secondary space-y-1"
            role="alert"
          >
            <p className="text-text-primary font-medium">
              Esta acción es irreversible.
            </p>
            <p>
              Al cerrar <span className="text-text-primary">{anioNum}</span>: el
              saldo arrastrado de las casas queda congelado en un snapshot y el
              año contable activo pasa a{' '}
              <span className="text-text-primary">{anioNum + 1}</span> (el
              dashboard y el cuadre rotan). El cierre no se puede deshacer.
            </p>
          </div>
        )}

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Para confirmar, escribe nuevamente el año a cerrar{' '}
            <span className="text-state-danger">*</span>
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={confirmStr}
            onChange={(e) => setConfirmStr(e.target.value)}
            placeholder="Repite el año"
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            required
          />
        </label>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
